import {
  createContext,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react'
import { useLocation } from '@reach/router'
import { useMobile } from 'src/hooks/useMobile'
import type { RecommendationsParams } from 'src/utils/recommendations/getShelfServerSideData'
import { ensureDYCookies } from 'src/utils/dynamicYield/dynamicYieldCookies'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { getDeviceInfo } from 'src/utils/dynamicYield/getUserDeviceObject'

import { clientStore } from '../../../../../services/axiosInstance'
import type { Product } from '../../types/product'
import { buildCategoriesProp } from './utils'

export type PageName =
  | 'product'
  | 'home'
  | 'emptysearch'
  | 'cart'
  | 'category'
  | 'pdd'
  | 'stores'
  | 'store'
  | 'collection'
  | 'landingpage'
  | 'myaccount'

type Engines = 'dy'

type RecommendationProviderProps = {
  children: React.ReactNode
  pageName: PageName
  product?: Product
  skuId?: string
  categorieName?: string
  engines: Engines[]
  /**
   * When true, the DY recommendations fetch is deferred until after the
   * window `load` event fires (or immediately if the page is already loaded).
   * Use this on the home page to avoid DY API calls blocking LCP/TBT.
   * Defaults to false (fetch fires immediately on mount).
   */
  deferUntilVisible?: boolean
}

type RecommendationContextProps = {
  dyDataRecommendations: any
  loading: boolean
  error: boolean
}

export const RecommendationContext = createContext<RecommendationContextProps>(
  {} as RecommendationContextProps
)

export const RecommendationProvider = ({
  children,
  product,
  pageName,
  skuId,
  categorieName,
  engines,
  deferUntilVisible = false,
}: RecommendationProviderProps) => {
  const { href } = useLocation()
  const {
    orderForm: { items },
    hasBeenInitialized,
  } = useCheckout()

  const { isMobile } = useMobile()

  const categories = buildCategoriesProp(product?.categoriesIds[0] ?? '')

  // Stable key for cart contents — empty string on non-cart pages so that
  // loading the cart doesn't retrigger DY fetches on home/PDP/category.
  const cartItemsKey = useMemo(
    () =>
      pageName === 'cart'
        ? items?.map((i) => `${i.productId}:${i.id}`).join('|') ?? ''
        : '',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageName, items]
  )

  const [dyDataRecommendations, setDyDataRecommendations] = useState(null)
  const [loading, setLoading] = useState(true)
  const isGetData = useRef(false)
  const [error, setError] = useState(false)
  const shouldUseTransition = pageName !== 'landingpage'

  const applyMaybeTransition = useCallback(
    (update: () => void) => {
      if (shouldUseTransition) {
        // Same transition as setDyDataRecommendations so both land in a single
        // commit instead of an urgent render for `loading` followed by a
        // second one for the data — doubles up on pages with more consumers
        // (PDP mounts GenericShelfv2 twice + CrossSell/CrossSellV2).
        startTransition(update)

        return
      }

      update()
    },
    [shouldUseTransition]
  )

  // Defer DY fetch until after load, then until the sentinel is visible.
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(!deferUntilVisible)

  useEffect(() => {
    if (!deferUntilVisible) {
      return
    }

    const startObserving = () => {
      const el = sentinelRef.current

      if (!el) {
        setIsVisible(true)

        return
      }

      const rootMarginPx = 200

      // Sync check matching observer rootMargin; avoids async wait when already in view.
      const rect = el.getBoundingClientRect()

      if (
        rect.bottom >= -rootMarginPx &&
        rect.top <= window.innerHeight + rootMarginPx
      ) {
        setIsVisible(true)

        return
      }

      // Fallback when the observer never fires on reload/hydration.
      const fallbackTimer = window.setTimeout(() => {
        setIsVisible(true)
      }, 1500)

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) {
            return
          }

          setIsVisible(true)
          window.clearTimeout(fallbackTimer)
          observer.disconnect()
        },
        { rootMargin: `${rootMarginPx}px` }
      )

      observer.observe(el)

      return () => {
        window.clearTimeout(fallbackTimer)
        observer.disconnect()
      }
    }

    // Wait for load before observing so DY calls do not compete with LCP.
    if (document.readyState === 'complete') {
      return startObserving()
    }

    let cleanupObserver: (() => void) | undefined

    const onLoad = () => {
      cleanupObserver = startObserving()
    }

    window.addEventListener('load', onLoad, { once: true })

    return () => {
      window.removeEventListener('load', onLoad)
      cleanupObserver?.()
    }
  }, [deferUntilVisible])

  const fetchDyRecommendations = useCallback(
    async (
      params: RecommendationsParams,
      dyid?: string,
      sessionId?: string
    ) => {
      const mustGetDYRecommendation = engines.includes('dy')

      if (!dyid || !sessionId || !mustGetDYRecommendation) {
        return
      }

      try {
        const device = await getDeviceInfo()
        const { data } = await clientStore.get(
          `/_v3/api/decathlon-search/pages/recommendations`,
          {
            params: {
              ...params,
              ...(skuId && { skuId }),
              engine: 'dy',
              device,
              ...(categorieName && { categoryNames: categorieName }),
            },
          }
        )

        applyMaybeTransition(() => {
          setDyDataRecommendations(data)
        })
      } catch (err) {
        console.error('Erro no DY:', err)
        setError(true)
      }
    },
    [engines, skuId, categorieName, applyMaybeTransition]
  )

  const recommendationKey = useMemo(
    () =>
      [
        pageName,
        href,
        skuId ?? '',
        categorieName ?? '',
        categories ?? '',
        isMobile === undefined ? 'u' : isMobile ? 'm' : 'd',
        cartItemsKey,
      ].join('::'),
    [pageName, href, skuId, categorieName, categories, isMobile, cartItemsKey]
  )

  useEffect(() => {
    setDyDataRecommendations(null)
    setLoading(true)
    setError(false)
    isGetData.current = false
  }, [recommendationKey])

  useEffect(() => {
    if (isGetData.current || isMobile === undefined || !isVisible) {
      return
    }

    if (pageName === 'cart' && !hasBeenInitialized) {
      return
    }

    if (dyDataRecommendations !== null) {
      applyMaybeTransition(() => {
        setLoading(false)
      })

      return
    }

    isGetData.current = true

    const buildBaseParams = async (): Promise<RecommendationsParams> => {
      const { dyid, sessionId } = await ensureDYCookies()

      const params: RecommendationsParams = {
        ...(categories && { categories }),
        source: isMobile ? 'mobile' : 'desktop',
        name: pageName,
        dysession: sessionId,
        dyuser: dyid,
        location: href,
        ...(pageName === 'cart' && {
          skuId: items?.map((item) => item.id) ?? [],
          productId: items?.map((item) => item.productId) ?? [],
        }),
      }

      return params
    }

    const getRecommendations = async () => {
      try {
        const { dyid, sessionId } = await ensureDYCookies()
        const params = await buildBaseParams()

        await Promise.all([fetchDyRecommendations(params, dyid, sessionId)])
      } catch (e) {
        console.error(e)
        setError(true)
      } finally {
        applyMaybeTransition(() => {
          setLoading(false)
        })
        isGetData.current = false
      }
    }

    getRecommendations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    recommendationKey,
    categories,
    href,
    isMobile,
    isVisible,
    cartItemsKey, // items → cartItemsKey: stable '' on non-cart pages
    pageName,
    hasBeenInitialized,
    applyMaybeTransition,
    dyDataRecommendations,
    fetchDyRecommendations,
  ])

  const recommendationContextValue = useMemo(
    () => ({
      product,
      dyDataRecommendations,
      loading,
      error,
    }),
    [product, dyDataRecommendations, loading, error]
  )

  return (
    <RecommendationContext.Provider value={recommendationContextValue}>
      {deferUntilVisible && (
        <div
          ref={sentinelRef}
          aria-hidden="true"
          style={{ height: 1, overflow: 'hidden', pointerEvents: 'none' }}
        />
      )}
      {children}
    </RecommendationContext.Provider>
  )
}
